import React from "react";
import * as echarts from "echarts";

function useEchart(option) {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // init once (no theme applied - keep default gray background)
    if (!chartRef.current) {
      chartRef.current = echarts.init(el, undefined, { renderer: "canvas" });
    }

    if (option) {
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
    }

    requestAnimationFrame(() => chartRef.current?.resize());

    const handle = () => chartRef.current?.resize();
    window.addEventListener("resize", handle);
    const ro = new ResizeObserver(handle);
    ro.observe(el);

    return () => {
      window.removeEventListener("resize", handle);
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
      requestAnimationFrame(() => chartRef.current?.resize());
    }
  }, [option]);

  return ref;
}

function parseBulletPoints(text) {
  if (!text || typeof text !== "string") return [];

  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("•"))
    .map((line) => line.substring(1).trim());
}

export default function BestPracticesGapsChart({ bestPractices, gaps }) {
  const practicesList = parseBulletPoints(bestPractices);
  const gapsList = parseBulletPoints(gaps);

  const option = React.useMemo(() => {
    const practicesCount = practicesList.length;
    const gapsCount = gapsList.length;
    const isDark = document.body.getAttribute('data-theme') === 'dark';
    const textColor = isDark ? '#e0e0e0' : '#333';

    return {
      title: {
        text: "Best Practices vs Gaps",
        left: "center",
        textStyle: { fontSize: 16, fontWeight: 600, color: textColor },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const param = params[0];
          const items =
            param.name === "Best Practices" ? practicesList : gapsList;

          if (!items.length) return `${param.name}: 0`;

          const listHtml = items
            .map((item) => `<li style="margin: 4px 0;">${item}</li>`)
            .join("");

          return `
            <strong>${param.name}: ${param.value}</strong>
            <ul style="margin: 8px 0; padding-left: 20px; max-width: 400px;">
              ${listHtml}
            </ul>
          `;
        },
      },
      grid: {
        left: 140,
        right: 60,
        top: 60,
        bottom: 40,
      },
      xAxis: {
        type: "value",
        name: "Count",
        nameLocation: "middle",
        nameGap: 25,
        minInterval: 1,
        axisLabel: { color: textColor },
        nameTextStyle: { color: textColor },
      },
      yAxis: {
        type: "category",
        data: ["Gaps", "Best Practices"],
        axisLabel: {
          fontSize: 13,
          fontWeight: 500,
          color: textColor,
        },
      },
      series: [
        {
          type: "bar",
          data: [
            {
              value: gapsCount,
              itemStyle: { color: "#e57373" },
            },
            {
              value: practicesCount,
              itemStyle: { color: "#66bb6a" },
            },
          ],
          label: {
            show: true,
            position: "right",
            formatter: "{c}",
            fontSize: 14,
            fontWeight: 600,
          },
          barWidth: "50%",
        },
      ],
    };
  }, [practicesList, gapsList]);

  const ref = useEchart(option);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
